import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import serviceDetails from './serviceDetails';
import serviceCosts from './serviceCosts';
import company from '../config/company';
import usePageMeta, {breadcrumbJsonLd} from '../seo/usePageMeta';
import './Pages.css';
import './ServiceDetail.css';

const ServiceDetail = ({serviceKey}) => {
  const detail = serviceDetails[serviceKey];
  const costs = serviceCosts[serviceKey];
  const path = `/leistungen/${serviceKey}`;

  const jsonLd = useMemo(() => {
    if (!detail) return null;
    return [
      breadcrumbJsonLd([
        {name: 'Startseite', path: '/'},
        {name: 'Leistungen', path: '/leistungen'},
        {name: detail.title, path}
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: detail.title,
        serviceType: detail.title,
        description: detail.lead,
        url: `${company.url}${path}`,
        provider: {'@id': `${company.url}/#organization`},
        areaServed: company.serviceArea.map((name) => ({'@type': 'City', name}))
      }
    ];
  }, [detail, path]);

  usePageMeta({
    title: detail ? `${detail.title} in Eitorf | Cura Care Pflegedienst` : 'Leistung nicht gefunden | Cura Care',
    description: detail ? `${detail.lead} ${detail.introParagraphs?.[0] || ''}`.slice(0, 158) : undefined,
    path,
    jsonLd,
    noindex: !detail
  });

  if (!detail) {
    return (
      <div className="page-view">
        <Header />
        <main className="page service-detail">
          <div className="page__inner">
            <div className="service-hero">
              <p className="service-hero__kicker">Leistung nicht gefunden</p>
              <h1>Diese Seite existiert nicht.</h1>
              <p className="service-hero__lead">
                Bitte wählen Sie eine unserer Leistungen aus der Übersicht aus oder kehren Sie zur Startseite zurück.
              </p>
              <div className="svc-actions">
                <Link className="svc-btn" to="/leistungen">Zur Leistungsübersicht</Link>
                <Link className="svc-btn svc-btn--ghost" to="/">Zur Startseite</Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-view">
      <Header />

      <main className="page service-detail" id={`service-${serviceKey}`}>
        <div className="page__inner">
          <nav className="breadcrumb" aria-label="Brotkrumen">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <Link to="/leistungen">Leistungen</Link>
            <span aria-hidden="true">/</span>
            <span>{detail.title}</span>
          </nav>

          <header className="service-hero">
            {detail.heroKicker && <p className="service-hero__kicker">{detail.heroKicker}</p>}
            <h1>{detail.heroTitle || detail.title}</h1>
            {detail.lead && <p className="service-hero__lead">{detail.lead}</p>}
            <div className="svc-actions">
              <Link className="svc-btn" to="/kontakt">Kostenlos beraten lassen</Link>
              <a className="svc-btn svc-btn--ghost" href={`tel:${company.phoneIntl}`}>
                {company.phone}
              </a>
            </div>
          </header>

          {detail.introParagraphs?.length ? (
            <section className="service-section">
              <div className="section-card service-card">
                <h2>Was Sie erwartet</h2>
                <div className="section-text">
                  {detail.introParagraphs.map((text) => (
                    <p key={text.slice(0, 20)}>{text}</p>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {costs ? (
            <section className="service-section" aria-labelledby={`costs-${serviceKey}`}>
              <div className="section-card service-card service-card--costs">
                <p className="section-kicker">Kosten und Kostenträger</p>
                <h2 id={`costs-${serviceKey}`}>{costs.title}</h2>
                <p className="section-text">{costs.text}</p>
                <Link className="svc-inline-link" to="/pflegekosten">Alle Beträge und Pflegegrade 2026</Link>
              </div>
            </section>
          ) : null}

          {detail.keyServices ? (
            <section className="service-section">
              <div className="section-card section-card--list service-card">
                <div>
                  <p className="section-kicker">Leistungsschwerpunkte</p>
                  <h2>{detail.keyServices.title}</h2>
                </div>
                <ul className="svc-list">
                  {detail.keyServices.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {detail.keyServices.note ? <p className="svc-note">{detail.keyServices.note}</p> : null}
              </div>
            </section>
          ) : null}

          {detail.contactSection ? (
            <section className="service-section">
              <div className="section-card service-card">
                <p className="section-kicker">Kontakt</p>
                <h2>{detail.contactSection.title}</h2>
                {detail.contactSection.text ? <p className="section-text">{detail.contactSection.text}</p> : null}
                <div className="svc-actions">
                  {detail.contactSection.phone ? (
                    <a className="svc-btn" href={`tel:${detail.contactSection.phone.replace(/\s/g, '')}`}>
                      Telefon: {detail.contactSection.phone}
                    </a>
                  ) : null}
                  {detail.contactSection.email ? (
                    <a className="svc-btn svc-btn--ghost" href={`mailto:${detail.contactSection.email}`}>
                      E-Mail: {detail.contactSection.email}
                    </a>
                  ) : null}
                  <Link className="svc-btn svc-btn--ghost" to="/kontakt">Kontaktformular</Link>
                </div>
                {detail.contactSection.formHint ? <p className="svc-note">{detail.contactSection.formHint}</p> : null}
              </div>
            </section>
          ) : null}

          {detail.sections?.map((section) => (
            <section className="service-section" key={section.title}>
              <div className="section-card service-card">
                <p className="section-kicker">Mehr zur Leistung</p>
                <h2>{section.title}</h2>
                <div className="section-text">
                  {section.paragraphs?.map((text) => (
                    <p key={text.slice(0, 20)}>{text}</p>
                  ))}
                </div>
                {section.items ? (
                  <ul className="svc-list svc-list--inline">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </section>
          ))}

          <section className="service-section">
            <div className="section-card section-card--cta service-card service-card--cta">
              <div>
                <p className="section-kicker">Nächster Schritt</p>
                <h2>Gemeinsam die passende Versorgung planen</h2>
                <p className="section-text">
                  Vereinbaren Sie ein unverbindliches Gespräch. Wir klären offene Fragen und erstellen einen individuellen Versorgungsplan.
                </p>
              </div>
              <div className="svc-actions service-card-actions">
                <Link className="svc-btn" to="/kontakt">Termin anfragen</Link>
                <Link className="svc-btn svc-btn--ghost" to="/leistungen">Alle Leistungen</Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
